// ============================================================================
// NavMessageBanner
// ----------------------------------------------------------------------------
// TS-060 / DEC-COUNCIL-015 T3 (QuoteMakerNavBar sub-slice): extracted VERBATIM;
// App keeps the `{navMessage && …}` gate and owns `dismissNavMessage` (passed as
// `onDismiss`); the action button's `onClick`/`busy`/`label` are carried on the
// `message.action` object App builds. Pure presentational, imports Icons.
// ============================================================================

import React from 'react';
import { Icons } from './Icon';

interface NavMessageBannerProps {
    /** The nav message to render: tone drives colours, icon keys into Icons, text is the body, action is an optional inline button, dismissible (default true) gates the × button. */
    message: {
        tone?: string;
        icon?: string;
        text: string;
        id: string | number;
        dismissible?: boolean;
        action?: { onClick: () => void; busy?: boolean; label: string };
    };
    /** Dismiss handler — App passes its `dismissNavMessage`; invoked with `message.id`. */
    onDismiss: (id: string | number) => void;
}

export function NavMessageBanner({ message, onDismiss }: NavMessageBannerProps) {
    // `hidden md:flex` hid the WHOLE channel below 768px. Defensible for the
    // two advisory tones (the nav bar has no room), but the error tone is the
    // only report that photo auto-assign failed on BOTH lanes (App.jsx
    // 'photo-auto-assign-failed') and it is mirrored to no other surface - a
    // narrow window swallowed it, and aria-live on a display:none node
    // announces nothing at any width.
    const isError = message.tone === 'error';
    return (
        <div
            className={`${isError ? 'flex' : 'hidden md:flex'} items-center gap-2 px-3 py-1 rounded-md relative z-10`}
            role={isError ? 'alert' : 'status'}
            aria-live={isError ? 'assertive' : 'polite'}
            style={{
                background: message.tone === 'warn' ? 'rgba(251,191,36,0.12)' :
                            message.tone === 'success' ? 'rgba(16,185,129,0.12)' :
                            'color-mix(in srgb, var(--qm-accent) 12%, transparent)',
                border: `1px solid ${message.tone === 'warn' ? 'rgba(251,191,36,0.35)' :
                                      message.tone === 'success' ? 'rgba(16,185,129,0.35)' :
                                      'color-mix(in srgb, var(--qm-accent) 35%, transparent)'}`,
                color: '#fff',
                fontSize: '11px',
                maxWidth: 'min(520px, 40vw)',
                flex: '0 1 auto',
                minWidth: 0,
            }}
        >
            {message.icon && Icons[message.icon] && (
                (() => { const I = Icons[message.icon]; return <I size={12} style={{ color: message.tone === 'warn' ? '#fbbf24' : message.tone === 'success' ? '#34d399' : 'var(--qm-accent)', flexShrink: 0 }} aria-hidden="true" />; })()
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: 0 }} title={message.text}>
                {message.text}
            </span>
            {message.action && (
                <button
                    onClick={message.action.onClick}
                    disabled={!!message.action.busy}
                    className="text-[10px] font-black uppercase tracking-wider"
                    style={{
                        padding: '2px 8px', borderRadius: '4px',
                        background: 'transparent',
                        border: '1px solid color-mix(in srgb, var(--qm-accent) 60%, transparent)',
                        color: 'var(--qm-accent)',
                        cursor: message.action.busy ? 'wait' : 'pointer',
                        opacity: message.action.busy ? 0.6 : 1,
                        flexShrink: 0,
                    }}
                >
                    {message.action.busy ? 'Working\u2026' : message.action.label}
                </button>
            )}
            {(message.dismissible !== false) && (
                <button
                    onClick={() => onDismiss(message.id)}
                    aria-label="Dismiss message"
                    className="text-[12px]"
                    style={{
                        background: 'transparent', border: 'none',
                        color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                        padding: '0 2px', flexShrink: 0,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                >
                    ×
                </button>
            )}
        </div>
    );
}
